import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useState } from "react"
import type { DTAVersion } from "../../../types/dta"
import { dtaKeys, useDtaDetail, useDtaVersions } from "../../../hooks/queries/useDtas"
import { DTADetails } from "../../../services/dtaApi"
import { ApiError } from "../../../services/sdk"

/**
 * Stable empty-versions reference so effects keyed on `versions` don't churn before data loads.
 */
const EMPTY_VERSIONS: DTAVersion[] = []

/**
 * Composes the DTA detail and version-history query hooks with the page-local UI state a data
 * stream needs (selected version, viewed stage, per-version loading). Data fetching and caching
 * live in the TanStack Query hooks; this hook owns only the view state and exposes cache-backed
 * setters so callers can apply optimistic updates through the query client.
 */
export function useDataStreamDetails(dtaId: string | undefined, urlVersion?: string) {
  const queryClient = useQueryClient()
  const detailQuery = useDtaDetail(dtaId, urlVersion)
  const versionsQuery = useDtaVersions(dtaId)

  const [selectedVersion, setSelectedVersion] = useState(urlVersion || "")
  const [viewedStage, setViewedStage] = useState<string | null>(null)
  const [versionLoading, setVersionLoading] = useState(false)

  const stream = detailQuery.data ?? null
  const versions = versionsQuery.data ?? EMPTY_VERSIONS

  // A 403 on a pinned version signals the page to redirect to the latest visible version, rather
  // than surfacing a generic error. Everything else is a real load error.
  const detailError = detailQuery.error
  const forbiddenVersion =
    detailError instanceof ApiError && detailError.status === 403 && urlVersion ? urlVersion : null
  const error =
    detailError && !forbiddenVersion
      ? detailError instanceof Error
        ? detailError.message
        : "Failed to load data stream"
      : null

  const detailKey = dtaKeys.detail(dtaId, urlVersion)
  const versionsKey = dtaKeys.versions(dtaId)

  // Cache-backed setters preserve the previous imperative API so callers can apply optimistic
  // updates. They write straight into the query cache the reads subscribe to.
  const setStream = useCallback(
    (update: DTADetails | null | ((prev: DTADetails | null) => DTADetails | null)) => {
      queryClient.setQueryData<DTADetails | null>(detailKey, (prev) =>
        typeof update === "function"
          ? (update as (p: DTADetails | null) => DTADetails | null)(prev ?? null)
          : update,
      )
    },
    [queryClient, detailKey],
  )

  const setVersions = useCallback(
    (update: DTAVersion[] | ((prev: DTAVersion[]) => DTAVersion[])) => {
      queryClient.setQueryData<{ versions: DTAVersion[]; count: number }>(versionsKey, (prev) => {
        const prevVersions = prev?.versions ?? []
        const next =
          typeof update === "function"
            ? (update as (p: DTAVersion[]) => DTAVersion[])(prevVersions)
            : update
        return { versions: next, count: next.length }
      })
    },
    [queryClient, versionsKey],
  )

  const refetch = useCallback(async () => {
    await detailQuery.refetch()
  }, [detailQuery])

  // Seed the selected version and viewed stage once the version list arrives. Prefer the
  // URL-requested version; otherwise keep a still-valid prior selection, else the latest.
  useEffect(() => {
    if (versions.length === 0) return
    const urlMatch = urlVersion ? versions.find((v) => v.version === urlVersion) : undefined
    setSelectedVersion((prev) =>
      urlMatch
        ? urlMatch.version
        : prev && versions.some((v) => v.version === prev)
          ? prev
          : versions[0].version,
    )
    if (urlMatch?.workflow_stage) setViewedStage(urlMatch.workflow_stage)
  }, [versions, urlVersion])

  // Keep the selected version's workflow_stage in sync with the stream after a transition, so the
  // version dropdown reflects the current stage without waiting for the versions refetch.
  useEffect(() => {
    if (!stream || !selectedVersion) return
    if (selectedVersion !== stream.version) return
    if (
      versions.some(
        (v) => v.version === selectedVersion && v.workflow_stage !== stream.workflow_stage,
      )
    ) {
      setVersions((prev) =>
        prev.map((v) =>
          v.version === selectedVersion ? { ...v, workflow_stage: stream.workflow_stage } : v,
        ),
      )
    }
    setViewedStage(stream.workflow_stage)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally reacting only to workflow_stage changes
  }, [stream?.workflow_stage, selectedVersion])

  return {
    stream,
    setStream,
    error,
    versions,
    setVersions,
    selectedVersion,
    setSelectedVersion,
    viewedStage,
    setViewedStage,
    versionLoading,
    setVersionLoading,
    forbiddenVersion,
    refetch,
  }
}
